import { FormEvent, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Mail, MessageCircle, Send } from 'lucide-react';
import { getCareConversations, markCareConversationRead, sendCareMessage, startCareConversation, type CareConversation, type MyFacility } from '../lib/api';
import './CareMessagesPage.css';

type LinkedPet = { petId: string; facilityId: string; petName: string; facilityName: string };

export default function CareMessagesPage({ facilities }: { facilities: MyFacility[] }) {
  const { getAccessTokenSilently } = useAuth0();
  const [conversations, setConversations] = useState<CareConversation[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [newConversationKey, setNewConversationKey] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const linkedPets: LinkedPet[] = facilities.flatMap((facility) => facility.pets.map((pet) => ({
    petId: pet.id,
    facilityId: facility.id,
    petName: pet.name,
    facilityName: facility.name,
  })));
  const selectedConversation = conversations.find((conversation) => conversation.id === selectedId) ?? null;
  const canStartConversation = linkedPets.length > 0;

  useEffect(() => {
    let active = true;
    void getAccessTokenSilently().then((token) => {
      if (!token) throw new Error('Missing access token');
      return getCareConversations(token);
    }).then((loaded) => {
      if (!active) return;
      setConversations(loaded);
      setSelectedId((current) => current || loaded[0]?.id || '');
    }).catch((loadError: unknown) => {
      if (active) setError(loadError instanceof Error ? loadError.message : 'Unable to load messages.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [getAccessTokenSilently]);

  const selectConversation = async (conversation: CareConversation) => {
    setSelectedId(conversation.id);
    setNotice('');
    if (!conversation.unread_count) return;
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      await markCareConversationRead(token, conversation.id);
      setConversations((current) => current.map((item) => item.id === conversation.id ? { ...item, unread_count: 0, messages: item.messages.map((message) => message.sender_role !== item.sender_role ? { ...message, read_at: new Date().toISOString() } : message) } : item));
    } catch {
      setError('Unable to mark messages as read.');
    }
  };

  const startConversation = async () => {
    const [facilityId, petId] = newConversationKey.split(':');
    if (!facilityId || !petId) return;
    setError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const conversation = await startCareConversation(token, facilityId, petId);
      setConversations((current) => [conversation, ...current.filter((item) => item.id !== conversation.id)]);
      setSelectedId(conversation.id);
      setNotice('Conversation ready');
      setBody('');
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'Unable to start the conversation.');
    }
  };

  const sendMessage = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedConversation || !body.trim()) return;
    setSending(true);
    setError('');
    setNotice('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const message = await sendCareMessage(token, selectedConversation.id, body.trim());
      setConversations((current) => current.map((item) => item.id === selectedConversation.id
        ? { ...item, updated_at: message.created_at, messages: [...item.messages, message] }
        : item).sort((left, right) => right.updated_at.localeCompare(left.updated_at)));
      setBody('');
      setNotice(message.email_delivery_error ? 'Message sent in Actapet. Email delivery is unavailable.' : 'Message sent and emailed.');
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Unable to send message.');
    } finally {
      setSending(false);
    }
  };

  return <>
    <div className="facility-page-heading"><div><p className="eyebrow">Care team</p><h1>Messages</h1><p>Private conversations between pet owners and facilities.</p></div></div>
    {error && <p className="form-error facility-error" role="alert">{error}</p>}
    <section className="care-messages-layout" aria-label="Care messages">
      <aside className="care-conversation-sidebar">
        <div className="care-conversation-heading"><h2>Conversations</h2><MessageCircle size={17} /></div>
        {canStartConversation && <div className="care-new-conversation"><label htmlFor="care-new-conversation">Start a message</label><select id="care-new-conversation" value={newConversationKey} onChange={(event) => setNewConversationKey(event.target.value)}><option value="">Choose a pet and facility</option>{linkedPets.map((link) => <option key={`${link.facilityId}:${link.petId}`} value={`${link.facilityId}:${link.petId}`}>{link.petName} · {link.facilityName}</option>)}</select><button type="button" className="button button-secondary" disabled={!newConversationKey} onClick={() => void startConversation()}>Open conversation</button></div>}
        {loading ? <p className="care-message-empty">Loading conversations...</p> : conversations.length === 0 ? <p className="care-message-empty">No conversations yet.</p> : <div className="care-conversation-list">{conversations.map((conversation) => <button type="button" className={`care-conversation-item ${selectedId === conversation.id ? 'active' : ''}`} key={conversation.id} onClick={() => void selectConversation(conversation)}><span className="care-conversation-pet">{conversation.pet.name}</span><span className="care-conversation-facility">{conversation.facility.name}</span><span className="care-conversation-preview">{conversation.messages.at(-1)?.body || 'No messages yet'}</span>{conversation.unread_count > 0 && <span className="care-unread-count">{conversation.unread_count}</span>}</button>)}</div>}
      </aside>
      <section className="care-thread-panel" aria-label="Selected conversation">
        {selectedConversation ? <>
          <header className="care-thread-header"><div><h2>{selectedConversation.pet.name}</h2><p>{selectedConversation.facility.name} · {selectedConversation.pet.species}</p></div><Mail size={18} /></header>
          <div className="care-thread-messages" aria-live="polite">{selectedConversation.messages.length === 0 ? <p className="care-message-empty">Start the conversation with {selectedConversation.facility.name}.</p> : selectedConversation.messages.map((message) => <article key={message.id} className={`care-message-bubble ${message.sender_role === selectedConversation.sender_role ? 'mine' : ''}`}><div className="care-message-meta"><strong>{message.sender_role === 'OWNER' ? selectedConversation.owner.name : selectedConversation.facility.name}</strong><time dateTime={message.created_at}>{new Date(message.created_at).toLocaleString()}</time></div><p>{message.body}</p>{message.email_delivery_error && <small>Email delivery failed; the message remains in Actapet.</small>}</article>)}</div>
          {notice && <p className="care-message-notice" role="status">{notice}</p>}
          <form className="care-message-composer" onSubmit={sendMessage}><label className="sr-only" htmlFor="care-message-body">Write a message</label><textarea id="care-message-body" value={body} onChange={(event) => setBody(event.target.value)} maxLength={4000} placeholder="Write a message..." required /><button className="button button-primary" disabled={sending || !body.trim()}><Send size={16} /> {sending ? 'Sending...' : 'Send'}</button></form>
        </> : <div className="care-thread-empty"><MessageCircle size={24} /><p>{loading ? 'Loading messages...' : 'Choose a conversation or start a message.'}</p></div>}
      </section>
    </section>
  </>;
}
